// Input numerico custom: type="text" con inputMode decimal per evitare
// i bug dell'input type="number" su mobile (cancellazione difficile, zeri forzati).
// Supporta cancellazione totale + placeholder + parsing locale (virgola o punto).
import { forwardRef, type InputHTMLAttributes } from 'react';
import { cn } from '@/utils';

interface NumberInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'onChange'> {
  value: string;
  onValueChange: (value: string) => void;
  onNumberChange?: (num: number) => void;
}

export const NumberInput = forwardRef<HTMLInputElement, NumberInputProps>(
  ({ className, value, onValueChange, onNumberChange, placeholder, ...props }, ref) => {
    function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
      // Pulisce l'input: solo cifre, virgola o punto (un solo separatore)
      let v = e.target.value;
      // Sostituisci virgola con punto per parsing, ma mostra ciò che l'utente scrive
      v = v.replace(/[^0-9.,]/g, '');
      // Solo un separatore: trova il primo e rimuovi i successivi
      const firstSep = v.search(/[.,]/);
      if (firstSep !== -1) {
        v = v.slice(0, firstSep + 1) + v.slice(firstSep + 1).replace(/[.,]/g, '');
      }
      onValueChange(v);
      if (onNumberChange) {
        const n = parseFloat(v.replace(',', '.'));
        onNumberChange(isNaN(n) ? 0 : n);
      }
    }

    return (
      <input
        ref={ref}
        type="text"
        inputMode="decimal"
        value={value}
        onChange={handleChange}
        placeholder={placeholder ?? '0,00'}
        className={cn(
          'w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-900',
          'placeholder:text-slate-400 transition-colors',
          'focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20',
          'disabled:cursor-not-allowed disabled:opacity-50',
          'dark:border-slate-700 dark:bg-slate-900/50 dark:text-slate-100 dark:placeholder:text-slate-500 dark:focus:border-brand-500',
          className,
        )}
        {...props}
      />
    );
  },
);
NumberInput.displayName = 'NumberInput';
